/**
 * Módulo principal de la interfaz de usuario (Frontend)
 * ====================================================
 * * Esta aplicación React gestiona la visualización de tasas de cambio en la República Dominicana.
 * Se conecta a Firebase Firestore para obtener datos en tiempo real y noticias financieras.
 * * Funcionalidades principales:
 * 1. Panel de control (Dashboard) con tasas de compra/venta.
 * 2. Gráficos históricos interactivos (Sparklines).
 * 3. Cintillo de noticias financieras en tiempo real.
 * 4. Calculadora de divisas.
 * 5. Soporte multi-idioma (Español/Inglés) y tema (Claro/Oscuro).
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { getAnalytics } from "firebase/analytics";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Label, Legend, CartesianGrid } from 'recharts';

// --- Importación de Logos ---
import logoLight from './assets/logo-light.png';
import logoDark from './assets/logo-dark.png';

// --- Configuración e inicialización de Firebase ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const analytics = getAnalytics(app);

// --- Constantes y configuraciones visuales ---

// Paleta de colores para los gráficos: una sola familia por tipo de tasa
// (dorado = compra, cobre = venta), variando intensidad por nivel de agregación.
// Validada con la skill dataviz (ramp ordinal, un solo matiz, monotona en luminosidad).
const CHART_COLORS = {
  light: {
    central: { buy: '#6B4F1E', sell: '#733222' },
    average: { buy: '#9C7530', sell: '#B85F3F' },
    entity:   { buy: '#C99940', sell: '#D08765' }
  },
  dark: {
    central: { buy: '#B08430', sell: '#A85434' },
    average: { buy: '#D4A657', sell: '#C97050' },
    entity:   { buy: '#EDCB86', sell: '#E6A588' }
  }
};

// Enlaces directos a los portales web de las entidades financieras
const bankLinks = {
  "Banco Central": "https://www.bancentral.gov.do/",
  "Banco BDI": "https://www.bdi.com.do/#",
  "Banco Caribe": "https://www.bancocaribe.com.do/",
  "Banco Ademi": "https://bancoademi.com.do/",
  "Banco Lopez de Haro": "https://www.blh.com.do/",
  "Banco Vimenca": "https://www.bancovimenca.com/",
  "Banesco": "https://www.banesco.com.do/",
  "Banco Lafise": "https://www.lafise.com/blrd/",
  "Banco Promerica": "https://www.promerica.com.do/",
  "Qik": "https://qik.do/",
  "BHD": "https://bhd.com.do/",
  "Banreservas": "https://banreservas.com/",
  "Scotiabank": "https://do.scotiabank.com/",
  "Banco Popular": "https://popularenlinea.com/",
  "APAP": "https://www.apap.com.do/",
  "Banco Santa Cruz": "https://www.bsc.com.do/",
  "Asoc. Nacional": "https://asociacionlanacional.com.do/",
  "Asoc. Cibao": "https://cibao.com.do/",
  "Asoc. Duarte": "https://adap.com.do/",
  "Asoc. Alaver": "https://alaver.com.do/",
  "ABONAP": "https://abonap.com.do/",
  "Asoc. Peravia": "https://asociacionperavia.com.do/",
  "Moneycorps": "https://www.moneycorps.com.do/",
  "CAPLA": "https://accapla.com/",
  "Caribe Express": "https://caribeexpress.com.do/",
  "Imbert & Balbuena": "https://ibc.com.do/",
  "Julio Díaz": "https://agentedecambiojuliodiaz.com/tasas-del-dia/",
  "ACN": "https://www.acn.com.do/",
  "Panora Exchange": "https://www.panora.do/",
  "Quezada": "https://quezada.do/",
  "Wally": "https://agentedecambiowally.com/",
  "RM": "https://www.infodolar.com.do/entidad-rm.aspx",
  "SCT": "https://www.infodolar.com.do/entidad-sct.aspx"
};

// --- Funciones auxiliares ---

/**
 * Determina si una entidad financiera es una asociación de ahorros y préstamos.
 * @param {string} name - El nombre de la entidad.
 * @returns {boolean} - Verdadero si es una asociación, falso en caso contrario.
 */
const isAssociationEntity = (name) => {
    if (!name) return false;
    const n = name.toLowerCase();
    return n.includes('asoc') || n.includes('apap') || n.includes('alaver') || n.includes('abonap');
};

// Agentes de cambio integrados a la publicación de tasas — lista explícita
// (sus nombres no comparten un patrón común como sí lo hacen las
// asociaciones) que debe coincidir con ENTIDADES_EN_PRUEBA en
// functions/api_datos/main.py y functions/generador_imagenes/main.py.
const AGENTES_CAMBIO = new Set(["Moneycorps", "CAPLA", "Caribe Express", "Imbert & Balbuena", "Julio Díaz", "ACN", "Panora Exchange", "Quezada", "Wally", "RM", "SCT"]);

/**
 * Determina si una entidad financiera es un agente de cambio.
 * @param {string} name - El nombre de la entidad.
 * @returns {boolean} - Verdadero si es un agente de cambio, falso en caso contrario.
 */
const isExchangeAgentEntity = (name) => !!name && AGENTES_CAMBIO.has(name);

/**
 * Homologa cualquier variante de "asociación" ("Asociación", "Asociacion",
 * "Asociado", "Asociada", con o sin tilde, en cualquier caja) al prefijo
 * abreviado "Asoc." — sin importar de dónde venga el nombre (Firestore,
 * caché vieja del navegador, etc.), la app siempre debe mostrar el mismo
 * formato para no confundir al usuario.
 * @param {string} name - El nombre de la entidad tal como llega.
 * @returns {string} - El nombre con el prefijo ya homologado.
 */
const normalizarNombreEntidad = (name) => {
    if (!name) return name;
    return name.replace(/^Asociaci[oó]n\.?\s+/i, 'Asoc. ').replace(/^Asociad[oa]\.?\s+/i, 'Asoc. ');
};

// --- Internacionalización (i18n) ---
const translations = {
  es: {
    appTitle: "Finanzas criollas",
    pageTitle: "Mercado cambiario de la República Dominicana",
    pageSubtitle: "Comparativa de las tasas de compra y venta del dólar de las entidades de intermediación financiera",
    lastUpdate: "Última actualización",
    disclaimer: "Las entidades financieras actualizan sus tasas cambiarias en distintos horarios, confirmar siempre en las fuentes oficiales (hacer clic en los nombres de las entidades).",
    priceDisclaimer: "El precio del dólar informado tienen un carácter orientativo, por lo que deben ser tomados únicamente a modo de referencia.",
    loading: "Cargando tasas de cambio...",
    loadingNews: "Cargando noticias...",
    notAvailable: "No disponible",
    error: "¡Error!",
    newsError: "No se pudieron cargar las noticias.",
    glossaryTitle: "Glosario de términos",
    understand: "Entendido",
    newsTitle: "Noticias del día:",
    bestRatesTitle: "Mejores tasas",
    centralBankTitle: "Banco Central",
    
    // Títulos y filtros de promedios
    averageRatesTitle: "Tasa promedio de entidades",
    avgFilters: {
        all: "Todos",
        banks: "Bancos múltiples",
        associations: "Asociaciones",
        agents: "Agentes de cambio"
    },
    centralBankLag: "Las tasas de referencia del banco central tienen un día de rezago.",
    centralBankCurrent: "Tasa oficial de referencia — vigente hoy.",
    centralBankStalePrefix: "Mostrando la última tasa disponible (",
    centralBankStaleSuffix: ") — el Banco Central aún no publica la de hoy.",

    exchangeRatesTitle: "Tasas de cambio por entidad",
    calculatorTitle: "Calculadora de divisas",
    contactTitle: "¿Quieres comunicarte con nosotros?",
    contactText: "Contáctanos a",
    buyRate: "Tasa de compra",
    sellRate: "Tasa de venta",
    averageRate: "Tasa promedio",
    spread: "Brecha",
    buy: "Compra",
    sell: "Venta",
    bank: "Entidad",
    date: "Fecha",
    rate: "Tasa",
    gap: "Brecha",
    systemAverage: "Promedio sistema",
    searchPlaceholder: "Buscar banco...",
    
    entityType: "Tipo de entidad",
    entityTypes: {
      all: "Todas",
      banks: "Bancos múltiples",
      associations: "Asociaciones",
      agents: "Agentes de cambio"
    },

    sortBy: "Organizar por",
    sortButton: "Organizar",
    sortOptions: {
      name: "Entidad",
      buyRate: "Tasa de compra",
      sellRate: "Tasa de venta",
      buyGap: "Brecha (compra)",
      sellGap: "Brecha (venta)"
    },
    iWant: "Quiero...",
    buyUSD: "Comprar USD",
    sellUSD: "Vender USD",
    rateApplied: "Tasa aplicada",
    amountUSD: "Monto en dólares (USD)",
    financialEntity: "Entidad financiera",
    equivalentDOP: "Equivalente en pesos (DOP)",
    highestBuyRate: "Tasa de compra más alta",
    lowestSellRate: "Tasa de venta más baja",
    glossaryTerms: [
      {
        term: "Tasa de compra",
        def: "Es el precio al que la entidad financiera (banco) te compra los dólares. Si tienes dólares y quieres pesos, esta es la tasa que te pagarán."
      },
      {
        term: "Tasa de venta",
        def: "Es el precio al que la entidad financiera te vende los dólares. Si tienes pesos y quieres comprar dólares, esta es la tasa que pagarás."
      },
      {
        term: "Tasa promedio",
        def: "Es el promedio calculado de las tasas de compra o venta de todas las entidades financieras listadas en el mercado spot para el día actual."
      },
      {
        term: "Brecha (Spread)",
        def: "Es la diferencia entre la tasa que ofrece un banco específico y la tasa de referencia del Banco Central. Una brecha positiva indica que el banco tiene una tasa mayor, y negativa que es menor."
      },
      {
        term: "Banco Central",
        def: "Es la entidad oficial que regula la política monetaria. Sus tasas de referencia son utilizadas para medir el comportamiento del mercado cambiario general."
      }
    ],
    linkTooltip: "Haz click para acceder a la página de la entidad",
    glossaryLink: "¿Qué significan estos términos? (Glosario)",
    dateLocale: 'es-ES',
    dateOptions: { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' },
    chartDateOptions: { month: 'short', day: 'numeric' },
    ago: "Hace",
    hours: "horas",
    days: "días",
    minutes: "minutos"
  },
  en: {
    appTitle: "Finanzas criollas", 
    pageTitle: "Dominican Republic Exchange Market",
    pageSubtitle: "Comparison of US Dollar buy and sell rates from financial intermediation entities",
    lastUpdate: "Last updated",
    disclaimer: "Financial entities update their exchange rates at different times, always confirm with official sources (click on entity names).",
    priceDisclaimer: "The exchange rate information provided is orientative in nature and should be used for reference purposes only.",
    loading: "Loading exchange rates...",
    loadingNews: "Loading news...",
    notAvailable: "Not available",
    error: "Error!",
    newsError: "Could not load news.",
    glossaryTitle: "Glossary of terms",
    understand: "Got It",
    newsTitle: "Today's News:",
    bestRatesTitle: "Best rates",
    centralBankTitle: "Central Bank",
    
    averageRatesTitle: "Average entity rate",
    avgFilters: {
        all: "All",
        banks: "Commercial banks",
        associations: "Associations",
        agents: "Exchange agents"
    },
    centralBankLag: "Central Bank reference rates have a one-day lag.",
    centralBankCurrent: "Official reference rate — in effect today.",
    centralBankStalePrefix: "Showing the latest available rate (",
    centralBankStaleSuffix: ") — the Central Bank hasn't published today's rate yet.",

    exchangeRatesTitle: "Exchange rates by entity",
    calculatorTitle: "Currency calculator",
    contactTitle: "Do you want to contact us?",
    contactText: "Contact us at",
    buyRate: "Buy rate",
    sellRate: "Sell rate",
    averageRate: "Average rate",
    spread: "Spread (gap)",
    buy: "Buy",
    sell: "Sell",
    bank: "Entity",
    date: "Date",
    rate: "Rate",
    gap: "Gap",
    systemAverage: "System average",
    searchPlaceholder: "Search bank...",
    
    entityType: "Entity type",
    entityTypes: {
      all: "All",
      banks: "Commercial banks",
      associations: "Associations",
      agents: "Exchange agents"
    },

    sortBy: "Sort by",
    sortButton: "Sort",
    sortOptions: {
      name: "Entity",
      buyRate: "Buy Rrate",
      sellRate: "Sell rate",
      buyGap: "Spread (buy)",
      sellGap: "Spread (sell)"
    },
    iWant: "I want to...",
    buyUSD: "Buy USD",
    sellUSD: "Sell USD",
    rateApplied: "Rate applied",
    amountUSD: "Amount in dollars (USD)",
    financialEntity: "Financial entity",
    equivalentDOP: "Equivalent in pesos (DOP)",
    highestBuyRate: "Highest buy rate",
    lowestSellRate: "Lowest sell rate",
    glossaryTerms: [
      {
        term: "Buy rate",
        def: "The price at which the financial entity (bank) buys US Dollars from you. If you have dollars and want pesos, this is the rate you will receive."
      },
      {
        term: "Sell rate",
        def: "The price at which the financial entity sells US Dollars to you. If you have pesos and want to buy dollars, this is the rate you will pay."
      },
      {
        term: "Average rate",
        def: "The calculated average of the buy or sell rates of all financial entities listed in the spot market for the current day."
      },
      {
        term: "Spread (gap)",
        def: "The difference between the rate offered by a specific bank and the reference rate of the Central Bank. A positive spread indicates the bank has a higher rate, and a negative one indicates a lower rate."
      },
      {
        term: "Central Bank",
        def: "The official entity that regulates monetary policy. Its reference rates are used to measure the general behavior of the exchange market."
      }
    ],
    linkTooltip: "Click to access the entity's website",
    glossaryLink: "What do these terms mean? (Glossary)",
    dateLocale: 'en-US',
    dateOptions: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
    chartDateOptions: { month: 'short', day: 'numeric' },
    ago: "ago",
    hours: "hours",
    days: "days",
    minutes: "minutes"
  }
};

// --- Componentes de interfaz (UI) ---

// Tooltip personalizado para el gráfico de Recharts
const CustomGraphTooltip = ({ active, payload, label, locale }) => {
  if (active && payload && payload.length) {
    const t = translations[locale];
    let dateStr = label;
    try {
       const dateObj = new Date(label + 'T00:00:00');
       dateStr = dateObj.toLocaleDateString(t.dateLocale, t.dateOptions);
    } catch(e) {}

    return (
      <div className="bg-ink/95 text-cream p-4 rounded-md text-sm font-mono border border-brass-deep/30 shadow-xl backdrop-blur-sm">
        <p className="label font-bold">{`${t.date}: ${dateStr}`}</p>
        {payload.map(pld => (
          <p key={pld.dataKey} style={{ color: pld.color }}>
            {`${pld.name}: ${pld.value.toFixed(2)}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Contenedor del gráfico que incluye el tooltip y el gráfico lineal
const ChartTooltip = ({ data, dataKey, strokeColor, bankName, locale, theme = 'dark' }) => {
  const t = translations[locale];
  const centralDataKey = dataKey === 'buy' ? 'centralBuy' : 'centralSell';
  const averageDataKey = dataKey === 'buy' ? 'avgBuy' : 'avgSell';

  const centralColor = dataKey === 'buy' ? CHART_COLORS[theme].central.buy : CHART_COLORS[theme].central.sell;
  const averageColor = dataKey === 'buy' ? CHART_COLORS[theme].average.buy : CHART_COLORS[theme].average.sell;

  const isGap = dataKey.includes('Gap'); 
  const lineName = isGap ? (dataKey === 'buyGap' ? `${t.spread} (${t.buy})` : `${t.spread} (${t.sell})`) : bankName;

  return (
    <div className="w-80 h-64 bg-paper-2 dark:bg-ink-2 p-2 rounded-lg shadow-xl border border-paper-line dark:border-ink-line text-ink dark:text-cream">
      <ResponsiveContainer width="100%" aspect={4/3} minWidth={0}>
        <LineChart data={data} margin={{ top: 5, right: 22, left: 20, bottom: 20 }} minWidth={0}>
          <CartesianGrid stroke="currentColor" strokeOpacity={0.12} vertical={false} />
          <XAxis
            dataKey="name" 
            stroke="currentColor" 
            fontSize={10} 
            tick={{ fill: 'currentColor' }} 
            tickLine={{ stroke: 'currentColor' }}
            tickFormatter={(value) => {
                try {
                    const dateObj = new Date(value + 'T00:00:00');
                    return dateObj.toLocaleDateString(t.dateLocale, t.chartDateOptions);
                } catch(e) { return value; }
            }}
          >
            <Label value={t.date} offset={-15} position="insideBottom" fill="currentColor" fontSize={12} />
          </XAxis>
          <YAxis domain={['dataMin - 0.2', 'dataMax + 0.2']} stroke="currentColor" fontSize={10} tick={{ fill: 'currentColor' }} tickLine={{ stroke: 'currentColor' }} tickFormatter={(value) => value.toFixed(1)}>
            <Label value={isGap ? t.gap : t.rate} angle={-90} position="insideLeft" fill="currentColor" fontSize={12} style={{ textAnchor: 'middle' }} />
          </YAxis>
          
          <Tooltip content={<CustomGraphTooltip locale={locale} />} />
          <Legend verticalAlign="top" height={40} iconType="line" wrapperStyle={{ fontSize: '11px' }} />

          {!isGap && (
            <>
              <Line type="monotone" dataKey={centralDataKey} name={t.centralBankTitle} stroke={centralColor} strokeWidth={2} dot={false} activeDot={{ r: 5, stroke: 'white', strokeWidth: 2, fill: centralColor }} />
              <Line type="monotone" dataKey={averageDataKey} name={t.systemAverage} stroke={averageColor} strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 5, stroke: 'white', strokeWidth: 2, fill: averageColor }} />
            </>
          )}
          
          <Line type="monotone" dataKey={dataKey} name={lineName} stroke={strokeColor} strokeWidth={3} dot={false} activeDot={{ r: 6, stroke: 'white', strokeWidth: 2, fill: strokeColor }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Envoltorio para manejar tooltips flotantes al pasar el mouse
const TooltipWrapper = ({ children, tooltipContent, align = 'center' }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      let leftPos;
      if (align === 'left') {
        leftPos = rect.left; 
      } else {
        leftPos = rect.left + (rect.width / 2); 
      }
      setPosition({ top: rect.top - 10, left: leftPos });
      setVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setVisible(false);
  };

  const transformClass = align === 'left' ? '-translate-y-full' : '-translate-x-1/2 -translate-y-full';

  return (
    <>
      <span ref={triggerRef} className="inline-block cursor-pointer" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </span>
      {visible && createPortal(
        <div className={`fixed z-50 pointer-events-none transform ${transformClass}`} style={{ top: position.top, left: position.left }}>
          <div className="mb-2">{tooltipContent}</div>
        </div>,
        document.body
      )}
    </>
  );
};

// Modal que muestra el glosario de términos financieros
const GlossaryModal = ({ isOpen, onClose, locale }) => {
  useEffect(() => {
    if (isOpen) { document.body.style.overflow = 'hidden'; } else { document.body.style.overflow = 'unset'; }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;
  const t = translations[locale];

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-paper-2 dark:bg-ink-2 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto border border-paper-line dark:border-ink-line transform transition-all scale-100" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-paper-line dark:border-ink-line sticky top-0 bg-paper-2 dark:bg-ink-2 z-10">
          <h3 className="text-2xl font-bold text-ink dark:text-cream">{t.glossaryTitle}</h3>
          <button onClick={onClose} className="text-mist-2 hover:text-ink dark:text-mist dark:hover:text-cream transition-colors p-1 rounded-full hover:bg-paper-line dark:hover:bg-ink-line">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-6">
          {t.glossaryTerms.map((item, index) => (
            <div key={index}>
              <h4 className="text-lg font-semibold text-brass-deep dark:text-brass mb-1">{item.term}</h4>
              <p className="text-mist-2 dark:text-mist text-base leading-relaxed">{item.def}</p>
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-paper-line dark:border-ink-line bg-paper dark:bg-ink/50 rounded-b-xl">
          <button onClick={onClose} className="w-full py-2 px-4 bg-brass-deep hover:bg-rust-deep text-cream font-semibold rounded-lg transition-colors">
            {t.understand}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Gráfico simplificado (sparkline) para mostrar tendencias
const SparkLineChart = ({ data, dataKey, strokeColor, seriesName, comparisonDataKey, comparisonSeriesName, comparisonStrokeColor = "#ff7300", locale }) => {
    const t = translations[locale];
    const dataLength = data && data.length ? data.length : 0;
    const interval = dataLength > 0 ? Math.floor(dataLength / 4) : 0;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 10, left: -25, bottom: 5 }}>
            <CartesianGrid stroke="currentColor" strokeOpacity={0.1} vertical={false} />
            <XAxis dataKey="name" stroke="currentColor" fontSize={12} tick={{ fill: 'currentColor' }} tickLine={{ stroke: 'currentColor' }} interval={interval} padding={{ left: 15, right: 10 }} tickFormatter={(value) => { try { const dateObj = new Date(value + 'T00:00:00'); return dateObj.toLocaleDateString(t.dateLocale, t.chartDateOptions); } catch(e) { return value; } }} />
            
            <YAxis yAxisId="left" domain={['dataMin - 0.1', 'dataMax + 0.1']} stroke="currentColor" fontSize={10} tick={{ fill: 'currentColor' }} tickLine={{ stroke: 'currentColor' }} tickFormatter={(value) => value.toFixed(1)} />
            
            <Tooltip content={<CustomGraphTooltip locale={locale} />} />
            {comparisonDataKey && ( <Legend verticalAlign="top" height={20} iconType="line" wrapperStyle={{ fontSize: '10px' }} /> )}
            {comparisonDataKey && ( <Line yAxisId="left" type="monotone" dataKey={comparisonDataKey} name={comparisonSeriesName} stroke={comparisonStrokeColor} strokeWidth={2} dot={{ r: 1, fill: comparisonStrokeColor }} activeDot={{ r: 4, stroke: 'white', strokeWidth: 1, fill: comparisonStrokeColor }} /> )}
            <Line yAxisId="left" type="monotone" dataKey={dataKey} name={seriesName} stroke={strokeColor} strokeWidth={2} dot={{ r: 1, fill: strokeColor }} activeDot={{ r: 4, stroke: 'white', strokeWidth: 1, fill: strokeColor }} />
            </LineChart>
        </ResponsiveContainer>
    );
};

// Función para renderizar el nombre del banco, posiblemente con un enlace
const renderBankName = (rawName) => {
    const name = normalizarNombreEntidad(rawName);
    const linkClasses = "hover:underline focus:outline-none focus:ring-2 focus:ring-brass-deep rounded";
    let url = bankLinks[name];
    if (!url) {
        if (!name) return <span>{name || ""}</span>;
        const lowerCaseName = name.toLowerCase();
        const matchingKey = Object.keys(bankLinks).find(key => lowerCaseName.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerCaseName));
        if (matchingKey) url = bankLinks[matchingKey];
    }
    if (!url) return <span>{name}</span>;

    const linkTooltipContent = (
      <div className="bg-ink text-cream text-base p-2 rounded shadow-md whitespace-nowrap">
        {translations[window.appLocale || 'es'].linkTooltip}
      </div>
    );

    return (
      <TooltipWrapper tooltipContent={linkTooltipContent} align="left">
          <a href={url} target="_blank" rel="noopener noreferrer" className={linkClasses} onClick={(e) => e.stopPropagation()}>
          {name}
          </a>
      </TooltipWrapper>
    );
};

// Indicador visual de cambio de tasa (flecha arriba/abajo)
const RateIndicator = ({ currentRate, previousRate }) => {
    // Entidades "solo compra" (ej. Caribe Express) nunca tienen tasa de
    // venta: currentRate/previousRate llegan null, no un número — se
    // verifica explícitamente en vez de depender de que "!previousRate"
    // capture también ese caso.
    if (currentRate == null || previousRate == null || currentRate === previousRate) {
        return <span className="ml-1 text-lg font-semibold text-mist-2 dark:text-mist">-</span>;
    }
    const isUp = parseFloat(currentRate) > parseFloat(previousRate);
    const color = isUp ? 'text-up' : 'text-down';
    const arrow = isUp ? '▲' : '▼';
    return <span className={`ml-1 text-lg font-semibold ${color}`}>{arrow}</span>;
};

// Valor de una tasa (compra o venta) con su indicador de tendencia, o un
// "-" simple si la entidad no tiene esa tasa (ej. Caribe Express no tiene
// venta) — evita mostrar "0.00" como si fuera un dato real, y evita el
// "-  -" duplicado que saldría de combinar un valor vacío con el "-" propio
// de RateIndicator.
const RateValue = ({ current, previous, className }) => {
    if (current == null) {
        return <span className={`${className} text-mist-2 dark:text-mist`}>-</span>;
    }
    return (
        <span className={className}>
            {current}
            <RateIndicator currentRate={current} previousRate={previous} />
        </span>
    );
};

// Componente para listar bancos en vista móvil (tarjetas)
const BankCardList = ({ sortedBankData, locale }) => {
    const t = translations[locale];
    return (
        <div className="grid grid-cols-1 gap-4 mt-4">
            {sortedBankData.map((bank) => (
                <div key={bank.id} className="bg-paper-2 dark:bg-ink-2 p-4 rounded-lg border border-paper-line dark:border-ink-line transition duration-150 ease-in-out hover:border-brass-deep/50 dark:hover:border-brass/50">
                    <div className="flex justify-between items-start border-b border-paper-line dark:border-ink-line pb-3 mb-3">
                        <div className="flex items-center space-x-3">
                            <img src={bank.logo} alt={`Logo de ${bank.name}`} className="w-8 h-8 rounded-full" />
                            <h4 className="text-xl font-bold text-ink dark:text-cream">{renderBankName(bank.name)}</h4>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-brass/10 dark:bg-brass/10 rounded-md">
                            <p className="text-sm font-medium text-mist-2 dark:text-mist mb-1">{t.buyRate}</p>
                            <RateValue current={bank.buyRateUSD.current} previous={bank.buyRateUSD.previous} className="text-2xl font-extrabold font-mono text-brass-deep dark:text-brass inline-flex items-center" />
                        </div>
                        <div className="text-center p-3 bg-rust/10 dark:bg-rust/10 rounded-md">
                            <p className="text-sm font-medium text-mist-2 dark:text-mist mb-1">{t.sellRate}</p>
                            <RateValue current={bank.sellRateUSD.current} previous={bank.sellRateUSD.previous} className="text-2xl font-extrabold font-mono text-rust-deep dark:text-rust inline-flex items-center" />
                        </div>
                    </div>
                    <div className="flex justify-between text-xs mt-3 pt-2 border-t border-paper-line dark:border-ink-line">
                        <p className="text-mist-2 dark:text-mist">
                            {t.spread} ({t.buy}):
                            <span className={`font-semibold font-mono ml-1 ${bank.buyGap == null ? 'text-mist-2 dark:text-mist' : (parseFloat(bank.buyGap) >= 0 ? 'text-up' : 'text-down')}`}>
                                {bank.buyGap || "-"}
                            </span>
                        </p>
                        <p className="text-mist-2 dark:text-mist">
                            {t.spread} ({t.sell}):
                            <span className={`font-semibold font-mono ml-1 ${bank.sellGap == null ? 'text-mist-2 dark:text-mist' : (parseFloat(bank.sellGap) <= 0 ? 'text-up' : 'text-down')}`}>
                                {bank.sellGap || "-"}
                            </span>
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Componente para la calculadora de conversión de divisas
const ExchangeCalculator = ({ banks, centralBank, locale }) => {
    const t = translations[locale];
    const [selectedBankId, setSelectedBankId] = useState('');
    const [mode, setMode] = useState('sell');
    const [amount, setAmount] = useState('');
    const [result, setResult] = useState('');

    const formatCurrency = (number) => {
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
    };

    // Solo entidades que sí tienen la tasa que aplica al modo elegido —
    // ej. Caribe Express solo compra divisas (no vende), así que no debe
    // aparecer como opción cuando el usuario quiere "Comprar USD" (mode
    // 'sell', que usa la tasa de venta de la entidad).
    const allEntities = useMemo(() => banks.filter(b =>
        mode === 'sell' ? b.sellRateUSD.current != null : b.buyRateUSD.current != null
    ), [banks, mode]);

    useEffect(() => {
      if (allEntities.length > 0 && !allEntities.find(b => b.id === selectedBankId)) {
          setSelectedBankId(allEntities[0].id);
      }
    }, [allEntities, selectedBankId]);

    const currentBank = allEntities.find(b => b.id === selectedBankId) || allEntities[0];
    const currentRate = currentBank ? (mode === 'sell' ? parseFloat(currentBank.sellRateUSD.current) : parseFloat(currentBank.buyRateUSD.current)) : 0;
  
    const handleAmountChange = (e) => {
      const val = e.target.value;
      setAmount(val);
      if (val && !isNaN(val) && currentRate > 0) {
        const numVal = parseFloat(val);
        setResult(formatCurrency(numVal * currentRate));
      } else {
        setResult('');
      }
    };
    
    useEffect(() => {
      if (amount && !isNaN(amount) && currentRate > 0) { setResult(formatCurrency(parseFloat(amount) * currentRate)); } else { setResult(''); }
    }, [mode, selectedBankId, currentRate, amount]);
  
    if (!currentBank) return null;
  
    return (
      <div className="bg-paper-2 dark:bg-ink-2 p-6 rounded-lg border border-paper-line dark:border-ink-line mt-8">
        <h3 className="text-2xl font-bold text-ink dark:text-cream mb-6 text-center">{t.calculatorTitle}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 align-top">
          <div>
             <label className="block text-xl font-medium text-mist-2 dark:text-mist mb-2">{t.iWant}</label>
             <div className="flex bg-paper dark:bg-ink-line rounded-lg p-1 h-[50px]"> 
              <button onClick={() => setMode('sell')} className={`flex-1 rounded-md text-xl font-medium transition-all flex items-center justify-center ${mode === 'sell' ? 'bg-paper-2 dark:bg-ink-2 text-brass-deep dark:text-brass shadow' : 'text-mist-2 dark:text-mist hover:text-ink dark:hover:text-cream'}`}>
                {t.buyUSD}
              </button>
              <button onClick={() => setMode('buy')} className={`flex-1 rounded-md text-xl font-medium transition-all flex items-center justify-center ${mode === 'buy' ? 'bg-paper-2 dark:bg-ink-2 text-brass-deep dark:text-brass shadow' : 'text-mist-2 dark:text-mist hover:text-ink dark:hover:text-cream'}`}>
                {t.sellUSD}
              </button>
            </div>
            <div className="mt-2 text-lg text-mist-2 dark:text-mist text-center h-4">
                {t.rateApplied}: <span className="font-mono">{currentRate.toFixed(2)}</span>
            </div>
          </div>
          <div>
            <label className="block text-xl font-medium text-mist-2 dark:text-mist mb-2">{t.amountUSD}</label>
            <div className="relative h-[50px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mist-2 dark:text-mist font-bold">$</span>
              <input type="number" value={amount} onChange={handleAmountChange} placeholder="0.00" className="w-full h-full pl-8 pr-4 rounded-lg border border-paper-line dark:border-ink-line bg-paper-2 dark:bg-ink-2 text-ink dark:text-cream font-mono focus:ring-2 focus:ring-brass-deep outline-none" />
            </div>
            <div className="mt-2 h-4"></div>
          </div>
          <div>
            <label className="block text-xl font-medium text-mist-2 dark:text-mist mb-2">{t.financialEntity}</label>
            <div className="h-[50px]">
              <select value={selectedBankId} onChange={(e) => setSelectedBankId(e.target.value)} className="w-full h-full px-3 rounded-lg border border-paper-line dark:border-ink-line bg-paper-2 dark:bg-ink-2 text-ink dark:text-cream focus:ring-2 focus:ring-brass-deep outline-none">
                {allEntities.map(bank => (
                  <option key={bank.id} value={bank.id}>{bank.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xl font-medium text-mist-2 dark:text-mist mb-2">{t.equivalentDOP}</label>
            <div className="w-full h-[50px] px-4 rounded-lg bg-brass/10 dark:bg-brass/10 border border-brass-deep/30 dark:border-brass/30 text-brass-deep dark:text-brass font-bold text-lg flex items-center justify-between">
              <span className="font-mono">RD$ {result || '0.00'}</span>
            </div>
          </div>
        </div>
      </div>
    );
};

// --- Componente de noticias tipo cintillo (ticker) ---
const NewsSection = ({ locale }) => {
  const t = translations[locale];
  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [errorNews, setErrorNews] = useState(null);

  useEffect(() => {
    // Se obtiene un límite mayor de noticias para poblar el cintillo
    const newsQuery = query(collection(db, 'financial_news'), orderBy('timestamp', 'desc'), limit(50));
    
    const unsubscribe = onSnapshot(newsQuery, (snapshot) => {
      const newsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNews(newsData);
      setLoadingNews(false);
    }, (error) => { console.error("Error fetching news:", error); setErrorNews(t.newsError); setLoadingNews(false); });
    return () => unsubscribe();
  }, [t.newsError]);

  if (loadingNews) return <div className="text-center text-mist-2 dark:text-mist my-4 h-12 flex items-center justify-center">{t.loadingNews}</div>;
  if (errorNews) return null;
  if (news.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          animation: marquee 480s linear infinite;
        }
        .hover-pause:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="mb-8 w-full bg-ink border-y-2 border-brass-deep overflow-hidden relative h-16 flex items-center group">
        
        {/* Contenedor del cintillo con animación */}
        <div className="flex animate-marquee hover-pause whitespace-nowrap">
            {/* Lista original de noticias */}
            {news.map((item) => (
                <a 
                   key={item.id} 
                   href={item.link} 
                   target="_blank" 
                   rel="noreferrer" 
                   className="flex items-center mx-8 text-xl text-cream/80 hover:text-cream transition-colors duration-200"
                >
                    <span className="font-bold font-mono text-brass mr-2">[{item.source}]</span>
                    <span>{locale === 'en' ? (item.title_en || item.title) : (item.title_es || item.title)}</span>
                    <span className="ml-8 text-mist">•</span>
                </a>
            ))}
            {/* Lista duplicada para efecto de bucle infinito */}
            {news.map((item) => (
                <a 
                   key={`${item.id}-dup`} 
                   href={item.link} 
                   target="_blank" 
                   rel="noreferrer" 
                   className="flex items-center mx-8 text-xl text-cream/80 hover:text-cream transition-colors duration-200"
                >
                    <span className="font-bold font-mono text-brass mr-2">[{item.source}]</span>
                    <span>{locale === 'en' ? (item.title_en || item.title) : (item.title_es || item.title)}</span>
                    <span className="ml-8 text-mist">•</span>
                </a>
            ))}
        </div>
      </div>
    </>
  );
};

// --- Componente principal del tablero de control (Dashboard) ---
const Dashboard = ({ 
    bankData, centralBankData, centralBankHistory, bestRates, 
    loading, lastUpdated, locale, isMobile, searchTerm, setSearchTerm, 
    selectedSortOption, setSelectedSortOption, sortConfig, setSortConfig, theme
}) => {
    const t = translations[locale];
    const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
    
    // Filtros de estado para la visualización de datos
    const [selectedEntityType, setSelectedEntityType] = useState('all');
    const [avgFilter, setAvgFilter] = useState('all');

    // Los agentes de cambio se activan solos cuando la API deja de ocultarlos
    // (ver FECHA_ACTIVACION_AGENTES en functions/api_datos/main.py) — el
    // frontend no repite esa fecha, solo reacciona a si ya llegan datos de
    // ese tipo para mostrar u ocultar el segmento/filtro correspondiente.
    const hasExchangeAgents = useMemo(
        () => !!bankData && bankData.some(b => isExchangeAgentEntity(b.name)),
        [bankData]
    );

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') { direction = 'descending'; } 
        else if (sortConfig.key !== key) {
          if (key.includes('Rate') || key.includes('Gap')) { direction = 'descending'; }
        }
        setSortConfig({ key, direction });
    };

    const handleSortButtonClick = (e) => { e.preventDefault(); requestSort(selectedSortOption); };
    const handleSortSelectChange = (e) => { e.preventDefault(); setSelectedSortOption(e.target.value); };
    const handleEntityTypeChange = (e) => { e.preventDefault(); setSelectedEntityType(e.target.value); };

    // Memorización de datos bancarios filtrados y ordenados
    const sortedBankData = useMemo(() => {
        if (!bankData) return [];

        let sortableData = [...bankData];
        
        // 1. Filtro por búsqueda de nombre
        if (searchTerm) { 
            sortableData = sortableData.filter(bank => 
                bank.name && bank.name.toLowerCase().includes(searchTerm.toLowerCase())
            ); 
        }

        // 2. Filtro por tipo de entidad
        if (selectedEntityType !== 'all') {
            sortableData = sortableData.filter(bank => {
                if (!bank.name) return false;
                if (selectedEntityType === 'associations') return isAssociationEntity(bank.name);
                if (selectedEntityType === 'agents') return isExchangeAgentEntity(bank.name);
                return !isAssociationEntity(bank.name) && !isExchangeAgentEntity(bank.name);
            });
        }

        // 3. Lógica de ordenamiento
        if (sortConfig.key) {
          sortableData.sort((a, b) => {
            const getNestedValue = (obj, path) => path.split('.').reduce((o, k) => (o || {})[k], obj);

            const aRaw = getNestedValue(a, sortConfig.key);
            const bRaw = getNestedValue(b, sortConfig.key);

            // Las entidades sin esa tasa (ej. Caribe Express sin venta) van
            // siempre al final, sin importar si el orden es ascendente o
            // descendente — no tiene sentido que "sin dato" gane el primer
            // lugar solo por invertir la dirección del ordenamiento.
            const aMissing = aRaw === null || aRaw === undefined;
            const bMissing = bRaw === null || bRaw === undefined;
            if (aMissing || bMissing) {
                if (aMissing && bMissing) return 0;
                return aMissing ? 1 : -1;
            }

            const aValue = aRaw || '';
            const bValue = bRaw || '';

            const aNum = parseFloat(aValue);
            const bNum = parseFloat(bValue);
            let aTransformed = aValue;
            let bTransformed = bValue;

            if (!isNaN(aNum) && !isNaN(bNum)) { 
                aTransformed = aNum; 
                bTransformed = bNum; 
            } else if (typeof aValue === 'string' && typeof bValue === 'string') { 
                aTransformed = aValue.toLowerCase(); 
                bTransformed = bValue.toLowerCase(); 
            }

            if (aTransformed < bTransformed) { return sortConfig.direction === 'ascending' ? -1 : 1; }
            if (aTransformed > bTransformed) { return sortConfig.direction === 'ascending' ? 1 : -1; }
            return 0;
          });
        }
        return sortableData;
    }, [bankData, sortConfig, searchTerm, selectedEntityType]);

    // --- Lógica para el cálculo de promedios ---
    const getAverageData = () => {
        let buyKey, sellKey;
        if (avgFilter === 'banks') {
            buyKey = 'avgBuyBanks'; sellKey = 'avgSellBanks';
        } else if (avgFilter === 'associations') {
            buyKey = 'avgBuyAsoc'; sellKey = 'avgSellAsoc';
        } else if (avgFilter === 'agents') {
            buyKey = 'avgBuyAgents'; sellKey = 'avgSellAgents';
        } else {
            buyKey = 'avgBuyAll'; sellKey = 'avgSellAll';
        }

        const lastEntry = centralBankHistory.length > 0 ? centralBankHistory[centralBankHistory.length - 1] : null;
        const prevEntry = centralBankHistory.length > 1 ? centralBankHistory[centralBankHistory.length - 2] : lastEntry;

        const currentBuy = lastEntry ? lastEntry[buyKey] : 0;
        const prevBuy = prevEntry ? prevEntry[buyKey] : 0;
        const currentSell = lastEntry ? lastEntry[sellKey] : 0;
        const prevSell = prevEntry ? prevEntry[sellKey] : 0;

        return {
            buy: { current: currentBuy?.toFixed(2) || '0.00', previous: prevBuy?.toFixed(2) || '0.00' },
            sell: { current: currentSell?.toFixed(2) || '0.00', previous: prevSell?.toFixed(2) || '0.00' },
            historyKeyBuy: buyKey,
            historyKeySell: sellKey
        };
    };

    const avgData = getAverageData();

    const getHeaderTooltip = (termKey) => {
        let searchTerm = t[termKey]; 
        const glossarySource = translations['es'].glossaryTerms; 
        const termData = glossarySource.find(item => item.term.toLowerCase() === searchTerm.toLowerCase() || item.term.toLowerCase().includes(searchTerm.toLowerCase()));
        let definition = termData ? t.glossaryTerms.find(item => item.term.toLowerCase().includes(t[termKey].toLowerCase()))?.def : t.notAvailable;
        if (!definition) definition = t.notAvailable;
        return ( <div className="bg-ink text-cream text-base p-2 rounded shadow-md max-w-xs normal-case font-normal whitespace-normal"> {definition} </div> );
    };

    const getSortIndicator = (key) => { if (sortConfig.key !== key) return null; return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'; };

    return (
        <>
        <div className="mb-4 text-left text-mist-2 dark:text-mist text-base md:text-xl">
          {t.lastUpdate}: <span className="font-semibold text-brass-deep dark:text-brass">{loading ? t.loading : (lastUpdated || t.notAvailable)}</span>
          <button onClick={() => setIsGlossaryOpen(true)} className="block mt-1 text-sm text-brass-deep dark:text-brass hover:underline focus:outline-none">
            {t.glossaryLink}
          </button>
          <p className="mt-2 text-xs text-mist-2 dark:text-mist italic"> {t.disclaimer} </p>
          <p className="mt-1 text-xs text-mist-2 dark:text-mist italic"> {t.priceDisclaimer} </p>
        </div>
        
        <GlossaryModal isOpen={isGlossaryOpen} onClose={() => setIsGlossaryOpen(false)} locale={locale} />
        
        <NewsSection locale={locale} />
        
        <div className="space-y-8 mb-8">
          {bestRates && (
            <div className="bg-paper-2 dark:bg-ink-2 p-4 rounded-lg border border-paper-line dark:border-ink-line">
              <h3 className="text-xl md:text-2xl font-bold text-ink dark:text-cream mb-4 text-center">{t.bestRatesTitle}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:divide-x md:divide-paper-line dark:md:divide-ink-line">
                {[
                  { key: 'banks', label: t.avgFilters.banks },
                  { key: 'associations', label: t.avgFilters.associations },
                  { key: 'agents', label: t.avgFilters.agents }
                ].filter(({ key }) => bestRates[key]).map(({ key, label }) => {
                  const group = bestRates[key];
                  return (
                    <div key={key} className="text-center md:px-4 first:md:pl-0 last:md:pr-0">
                      <p className="text-sm font-semibold text-mist-2 dark:text-mist mb-4 uppercase tracking-wide">{label}</p>
                      <div className="space-y-5">
                        {group.highestBuy && (
                          <div>
                            <p className="text-lg font-medium text-mist-2 dark:text-mist mb-1">{t.highestBuyRate}</p>
                            <span className="text-3xl font-semibold font-mono text-brass-deep dark:text-brass">{group.highestBuy.rate}</span>
                            <div className="mt-2 flex flex-wrap justify-center gap-1 text-xl text-mist-2 dark:text-mist">
                                {group.highestBuy.names.map((name, index) => (
                                    <span key={name}>
                                        {renderBankName(name)}
                                        {index < group.highestBuy.names.length - 1 && <span className="mr-1">,</span>}
                                    </span>
                                ))}
                            </div>
                          </div>
                        )}
                        {group.lowestSell && (
                          <div>
                            <p className="text-lg font-medium text-mist-2 dark:text-mist mb-1">{t.lowestSellRate}</p>
                            <span className="text-3xl font-semibold font-mono text-rust-deep dark:text-rust">{group.lowestSell.rate}</span>
                            <div className="mt-2 flex flex-wrap justify-center gap-1 text-xl text-mist-2 dark:text-mist">
                                {group.lowestSell.names.map((name, index) => (
                                    <span key={name}>
                                        {renderBankName(name)}
                                        {index < group.lowestSell.names.length - 1 && <span className="mr-1">,</span>}
                                    </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {centralBankData && (
              <div className="bg-paper-2 dark:bg-ink-2 p-4 rounded-lg border border-paper-line dark:border-ink-line flex flex-col">
                <h3 className="text-xl md:text-2xl font-bold text-ink dark:text-cream mb-2 text-center">{renderBankName(translations['es'].centralBankTitle)}</h3> 
                
                {/* --- Nota sobre la vigencia del Banco Central: dinámica,
                     porque desde que el scraper busca fecha de vigencia
                     exacta, la mayoría de los días SÍ es la tasa de hoy y ya
                     no aplica un aviso de "rezago" genérico. Solo se avisa
                     cuando de verdad se está mostrando la de un día anterior. --- */}
                <div className="flex justify-center mb-4">
                     <p className="text-xs text-mist-2 dark:text-mist italic text-center">
                        {centralBankData.desactualizado
                          ? `${t.centralBankStalePrefix}${centralBankData.fechaVigencia}${t.centralBankStaleSuffix}`
                          : t.centralBankCurrent}
                     </p>
                </div>

                <div className="w-full space-y-4">
                  <div className="w-full flex flex-col md:flex-row items-center md:space-x-4 space-y-4 md:space-y-0">
                    <div className="w-full md:w-1/2 text-center flex flex-col justify-center"> 
                      <p className="text-lg md:text-xl font-medium text-mist-2 dark:text-mist mb-1">{t.sellRate}</p>
                      <span className="text-2xl md:text-3xl font-semibold font-mono text-ink dark:text-cream block">
                        {centralBankData.sellRateUSD.current}
                        <RateIndicator currentRate={centralBankData.sellRateUSD.current} previousRate={centralBankData.sellRateUSD.previous}/>
                      </span>
                    </div>
                    <div className="w-full md:w-1/2 h-48 md:h-32 text-ink dark:text-cream block"> 
                      <SparkLineChart data={centralBankHistory} dataKey="centralSell" strokeColor={CHART_COLORS[theme].central.sell} seriesName={t.sellRate} locale={locale} />
                    </div>
                  </div>
                  <div className="w-full flex flex-col md:flex-row items-center md:space-x-4 space-y-4 md:space-y-0">
                    <div className="w-full md:w-1/2 text-center flex flex-col justify-center">
                      <p className="text-lg md:text-xl font-medium text-mist-2 dark:text-mist mb-1">{t.buyRate}</p>
                      <span className="text-2xl md:text-3xl font-semibold font-mono text-ink dark:text-cream block">
                        {centralBankData.buyRateUSD.current}
                        <RateIndicator currentRate={centralBankData.buyRateUSD.current} previousRate={centralBankData.buyRateUSD.previous}/>
                      </span>
                    </div>
                    <div className="w-full md:w-1/2 h-48 md:h-32 text-ink dark:text-cream block"> 
                      <SparkLineChart data={centralBankHistory} dataKey="centralBuy" strokeColor={CHART_COLORS[theme].central.buy} seriesName={t.buyRate} locale={locale} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {centralBankHistory.length > 0 && (
              <div className="bg-paper-2 dark:bg-ink-2 p-4 rounded-lg border border-paper-line dark:border-ink-line flex flex-col">
                <h3 className="text-xl md:text-2xl font-bold text-ink dark:text-cream mb-2 text-center">{t.averageRatesTitle}</h3>
                
                {/* --- Botones de filtro para promedios --- */}
                <div className="w-full flex justify-center flex-wrap gap-2 mb-4">
                      <button onClick={() => setAvgFilter('all')} className={`px-3 py-1 rounded text-xl transition-colors ${avgFilter === 'all' ? 'bg-brass-deep text-cream' : 'bg-paper dark:bg-ink-line text-ink dark:text-cream hover:bg-paper-line dark:hover:bg-ink-2'}`}>
                        {t.avgFilters.all}
                      </button>
                      <button onClick={() => setAvgFilter('banks')} className={`px-3 py-1 rounded text-xl transition-colors ${avgFilter === 'banks' ? 'bg-brass-deep text-cream' : 'bg-paper dark:bg-ink-line text-ink dark:text-cream hover:bg-paper-line dark:hover:bg-ink-2'}`}>
                        {t.avgFilters.banks}
                      </button>
                      <button onClick={() => setAvgFilter('associations')} className={`px-3 py-1 rounded text-xl transition-colors ${avgFilter === 'associations' ? 'bg-brass-deep text-cream' : 'bg-paper dark:bg-ink-line text-ink dark:text-cream hover:bg-paper-line dark:hover:bg-ink-2'}`}>
                        {t.avgFilters.associations}
                      </button>
                      {hasExchangeAgents && (
                        <button onClick={() => setAvgFilter('agents')} className={`px-3 py-1 rounded text-xl transition-colors ${avgFilter === 'agents' ? 'bg-brass-deep text-cream' : 'bg-paper dark:bg-ink-line text-ink dark:text-cream hover:bg-paper-line dark:hover:bg-ink-2'}`}>
                          {t.avgFilters.agents}
                        </button>
                      )}
                </div>

                <div className="w-full space-y-4">
                  <div className="w-full flex flex-col md:flex-row items-center md:space-x-4 space-y-4 md:space-y-0">
                    <div className="w-full md:w-1/2 text-center flex flex-col justify-center">
                      <p className="text-lg md:text-xl font-medium text-mist-2 dark:text-mist mb-1">{t.sellRate}</p>
                      <span className="text-2xl md:text-3xl font-semibold font-mono text-ink dark:text-cream block">
                        {avgData.sell.current}
                        <RateIndicator currentRate={avgData.sell.current} previousRate={avgData.sell.previous} />
                      </span>
                    </div>
                    <div className="w-full md:w-1/2 h-48 md:h-32 text-ink dark:text-cream block"> 
                      <SparkLineChart data={centralBankHistory} dataKey={avgData.historyKeySell} strokeColor={CHART_COLORS[theme].average.sell} seriesName={t.sellRate} locale={locale} />
                    </div>
                  </div>
                  <div className="w-full flex flex-col md:flex-row items-center md:space-x-4 space-y-4 md:space-y-0">
                    <div className="w-full md:w-1/2 text-center flex flex-col justify-center">
                      <p className="text-lg md:text-xl font-medium text-mist-2 dark:text-mist mb-1">{t.buyRate}</p>
                      <span className="text-2xl md:text-3xl font-semibold font-mono text-ink dark:text-cream block">
                        {avgData.buy.current}
                        <RateIndicator currentRate={avgData.buy.current} previousRate={avgData.buy.previous} />
                      </span>
                    </div>
                    <div className="w-full md:w-1/2 h-48 md:h-32 text-ink dark:text-cream block"> 
                      <SparkLineChart data={centralBankHistory} dataKey={avgData.historyKeyBuy} strokeColor={CHART_COLORS[theme].average.buy} seriesName={t.buyRate} locale={locale} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <ExchangeCalculator banks={bankData} centralBank={centralBankData} locale={locale} />

        <div className="rounded-lg border border-paper-line dark:border-ink-line mt-8">
          <div className="p-5 bg-paper-2 dark:bg-ink-2 border-b border-paper-line dark:border-ink-line rounded-t-lg flex flex-col xl:flex-row justify-between items-center space-y-3 xl:space-y-0">
            <h2 className="text-xl md:text-2xl font-bold text-ink dark:text-cream text-center xl:text-left">{t.exchangeRatesTitle}</h2>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full xl:w-auto justify-center xl:justify-end">
                {isMobile && (
                    <input type="text" placeholder={t.searchPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-3 py-2 rounded border border-paper-line dark:border-ink-line bg-paper-2 dark:bg-ink-2 text-ink dark:text-cream text-sm focus:outline-none focus:ring-1 focus:ring-brass-deep" />
                )}

                <div className="flex items-center justify-between sm:justify-start space-x-2 w-full sm:w-auto">
                    <span className="text-sm text-mist-2 dark:text-mist font-medium whitespace-nowrap">{t.entityType}:</span>
                    <select value={selectedEntityType} onChange={handleEntityTypeChange} className="px-2 py-1 rounded border border-paper-line dark:border-ink-line bg-paper-2 dark:bg-ink-2 text-ink dark:text-cream text-sm focus:outline-none focus:ring-1 focus:ring-brass-deep w-full max-w-[150px] sm:max-w-none">
                        <option value="all">{t.entityTypes.all}</option>
                        <option value="banks">{t.entityTypes.banks}</option>
                        <option value="associations">{t.entityTypes.associations}</option>
                        {hasExchangeAgents && <option value="agents">{t.entityTypes.agents}</option>}
                    </select>
                </div>

                <div className="flex items-center justify-between sm:justify-start space-x-2 w-full sm:w-auto">
                    <span className="text-sm text-mist-2 dark:text-mist font-medium whitespace-nowrap">{t.sortBy}:</span>
                    <select value={selectedSortOption} onChange={handleSortSelectChange} className="px-2 py-1 rounded border border-paper-line dark:border-ink-line bg-paper-2 dark:bg-ink-2 text-ink dark:text-cream text-sm focus:outline-none focus:ring-1 focus:ring-brass-deep w-full max-w-[150px] sm:max-w-none">
                        <option value="name">{t.sortOptions.name}</option>
                        <option value="buyRateUSD.current">{t.sortOptions.buyRate}</option>
                        <option value="sellRateUSD.current">{t.sortOptions.sellRate}</option>
                        <option value="buyGap">{t.sortOptions.buyGap}</option>
                        <option value="sellGap">{t.sortOptions.sellGap}</option>
                    </select>
                    <button type="button" onClick={handleSortButtonClick} className="px-3 py-1 bg-brass-deep hover:bg-rust-deep text-cream text-sm rounded transition-colors flex items-center justify-center">
                        {isMobile ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                        ) : (
                            t.sortButton
                        )}
                    </button>
                </div>
            </div>
          </div>
          
          {isMobile ? ( <BankCardList sortedBankData={sortedBankData} locale={locale} /> ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-paper-line dark:divide-ink-line">
                <thead className="bg-paper-2 dark:bg-ink-2">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-center text-xl font-medium text-mist-2 dark:text-mist tracking-wider cursor-pointer hover:bg-paper-line dark:hover:bg-ink-line transition-colors" onClick={() => requestSort('name')}> {t.bank} {getSortIndicator('name')} </th>
                    <th scope="col" className="px-4 py-3 text-center text-xl font-medium text-mist-2 dark:text-mist tracking-wider cursor-pointer hover:bg-paper-line dark:hover:bg-ink-line transition-colors" onClick={() => requestSort('buyRateUSD.current')}> <TooltipWrapper tooltipContent={getHeaderTooltip("buyRate")}> <span>{t.buyRate}</span> </TooltipWrapper> {getSortIndicator('buyRateUSD.current')} </th>
                    <th scope="col" className="px-4 py-3 text-center text-xl font-medium text-mist-2 dark:text-mist tracking-wider cursor-pointer hover:bg-paper-line dark:hover:bg-ink-line transition-colors" onClick={() => requestSort('sellRateUSD.current')}> <TooltipWrapper tooltipContent={getHeaderTooltip("sellRate")}> <span>{t.sellRate}</span> </TooltipWrapper> {getSortIndicator('sellRateUSD.current')} </th>
                    <th scope="col" className="px-4 py-3 text-center text-xl font-medium text-mist-2 dark:text-mist tracking-wider cursor-pointer hover:bg-paper-line dark:hover:bg-ink-line transition-colors" onClick={() => requestSort('buyGap')}> <TooltipWrapper tooltipContent={getHeaderTooltip("spread")}> <span>{t.spread} ({t.buy})</span> </TooltipWrapper> {getSortIndicator('buyGap')} </th>
                    <th scope="col" className="px-4 py-3 text-center text-xl font-medium text-mist-2 dark:text-mist tracking-wider cursor-pointer hover:bg-paper-line dark:hover:bg-ink-line transition-colors" onClick={() => requestSort('sellGap')}> <TooltipWrapper tooltipContent={getHeaderTooltip("spread")}> <span>{t.spread} ({t.sell})</span> </TooltipWrapper> {getSortIndicator('sellGap')} </th>
                  </tr>
                </thead>
                <tbody className="bg-paper-2 dark:bg-ink-2 divide-y divide-paper-line dark:divide-ink-line">
                  {sortedBankData.map((bank) => (
                    <tr key={bank.id} className="hover:bg-paper dark:hover:bg-ink-line/40 transition duration-150 ease-in-out">
                      <td className="px-4 py-4 whitespace-nowrap text-xl font-medium text-ink dark:text-cream text-left">
                        <div className="flex items-center space-x-2"> <img src={bank.logo} alt={`Logo de ${bank.name}`} className="w-6 h-6 rounded-full" /> {renderBankName(bank.name)} </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-xl text-center text-ink dark:text-cream">
                        <TooltipWrapper tooltipContent={<ChartTooltip data={bank.history} dataKey="buy" strokeColor={CHART_COLORS[theme].entity.buy} bankName={bank.name} locale={locale} theme={theme} />}>
                          <RateValue current={bank.buyRateUSD.current} previous={bank.buyRateUSD.previous} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-lg font-semibold font-mono text-brass-deep dark:text-brass" />
                        </TooltipWrapper>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-xl text-center text-ink dark:text-cream">
                        <TooltipWrapper tooltipContent={<ChartTooltip data={bank.history} dataKey="sell" strokeColor={CHART_COLORS[theme].entity.sell} bankName={bank.name} locale={locale} theme={theme} />}>
                          <RateValue current={bank.sellRateUSD.current} previous={bank.sellRateUSD.previous} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-lg font-semibold font-mono text-rust-deep dark:text-rust" />
                        </TooltipWrapper>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-xl text-center font-mono text-ink dark:text-cream"> <TooltipWrapper tooltipContent={<ChartTooltip data={bank.history} dataKey="buyGap" strokeColor={CHART_COLORS[theme].entity.buy} bankName={bank.name} locale={locale} theme={theme} />}> {bank.buyGap || "-"} </TooltipWrapper> </td>
                      <td className="px-4 py-4 whitespace-nowrap text-xl text-center font-mono text-ink dark:text-cream"> <TooltipWrapper tooltipContent={<ChartTooltip data={bank.history} dataKey="sellGap" strokeColor={CHART_COLORS[theme].entity.sell} bankName={bank.name} locale={locale} theme={theme} />}> {bank.sellGap || "-"} </TooltipWrapper> </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div className="mt-8 mb-4 p-6 bg-paper-2 dark:bg-ink-2 rounded-lg border border-paper-line dark:border-ink-line text-center">
            <h3 className="text-xl font-bold text-ink dark:text-cream mb-2">{t.contactTitle}</h3>
            <p className="text-mist-2 dark:text-mist">
                {t.contactText} <a href="mailto:datoscriollos.rd@gmail.com" className="font-semibold text-brass-deep dark:text-brass hover:underline">datoscriollos.rd@gmail.com</a>
            </p>
        </div>
        </>
    );
};

// --- Componente principal (App) ---
function App() {
  const [bankData, setBankData] = useState([]);
  const [centralBankData, setCentralBankData] = useState(null);
  const [centralBankHistory, setCentralBankHistory] = useState([]);
  const [averageRates, setAverageRates] = useState(null);
  const [bestRates, setBestRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState('');
  const [theme, setTheme] = useState('dark');
  const [locale, setLocale] = useState('es'); 
  const [isMobile, setIsMobile] = useState(false);
  const [selectedSortOption, setSelectedSortOption] = useState('buyRateUSD.current'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'buyRateUSD.current', direction: 'descending' });

  const t = translations[locale]; 
  window.appLocale = locale; 

  useEffect(() => {
    const handleResize = () => { setIsMobile(window.innerWidth < 768); };
    handleResize(); 
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Carga inicial de datos desde Firebase
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const dashboardResponse = await fetch('/api-frontend/dashboard');
        if (!dashboardResponse.ok) throw new Error("No se pudo obtener el panel de tasas.");
        const dashboardData = await dashboardResponse.json();
        const dates = dashboardData.days.map(d => d.date);
        if (dates.length === 0) throw new Error("No se encontraron datos históricos.");
        const allDaysEntities = dashboardData.days.map(d => d.entities);
        const bankHistoricalData = {};
        const summaryHistoricalData = [];

        allDaysEntities.forEach((entities, index) => {
          const dateName = dates[index];
          entities.forEach(entity => {
            const name = entity.nombre_entidad;
            if (!bankHistoricalData[name]) bankHistoricalData[name] = [];
            // null (no NaN) cuando la entidad no tiene esa tasa — ej. Caribe
            // Express solo compra divisas, nunca tiene tasa_venta. Con NaN
            // el "0.00"/"NaN" se cuela más adelante como si fuera un dato
            // real; con null, cada punto de esta serie se puede filtrar
            // explícitamente (o dejar como hueco en el gráfico, igual que ya
            // se hace con el Banco Central).
            bankHistoricalData[name].push({
                name: dateName,
                buy: entity.tasa_compra != null ? parseFloat(entity.tasa_compra) : null,
                sell: entity.tasa_venta != null ? parseFloat(entity.tasa_venta) : null
            });
          });
          
          const centralBank = entities.find(e => e.nombre_entidad === "Banco Central");
          const otherBanks = entities.filter(e => e.nombre_entidad !== "Banco Central");
          
          // Cálculo de promedios separados por tipo de entidad
          const associations = otherBanks.filter(b => isAssociationEntity(b.nombre_entidad));
          const exchangeAgents = otherBanks.filter(b => isExchangeAgentEntity(b.nombre_entidad));
          const commercialBanks = otherBanks.filter(b => !isAssociationEntity(b.nombre_entidad) && !isExchangeAgentEntity(b.nombre_entidad));

          const calculateAvg = (list, key) => {
             const validRates = list.map(b => parseFloat(b[key])).filter(r => r > 0);
             if (validRates.length === 0) return 0;
             return validRates.reduce((s, r) => s + r, 0) / validRates.length;
          };

          const avgBuyAll = calculateAvg(otherBanks, 'tasa_compra');
          const avgSellAll = calculateAvg(otherBanks, 'tasa_venta');

          const avgBuyBanks = calculateAvg(commercialBanks, 'tasa_compra');
          const avgSellBanks = calculateAvg(commercialBanks, 'tasa_venta');

          const avgBuyAsoc = calculateAvg(associations, 'tasa_compra');
          const avgSellAsoc = calculateAvg(associations, 'tasa_venta');

          const avgBuyAgents = calculateAvg(exchangeAgents, 'tasa_compra');
          const avgSellAgents = calculateAvg(exchangeAgents, 'tasa_venta');

          // null (no 0) cuando falta el dato del BC ese día — un 0 real
          // arrastra el eje Y de cualquier gráfico que use esta serie hasta
          // cero, aplastando la variación real en una línea casi plana con
          // una caída falsa al final. Con null, Recharts simplemente deja
          // un hueco en ese punto en vez de mentir con una caída a cero.
          const centralBuyVal = centralBank?.tasa_compra ? parseFloat(centralBank.tasa_compra) : null;
          const centralSellVal = centralBank?.tasa_venta ? parseFloat(centralBank.tasa_venta) : null;
          
          summaryHistoricalData.push({ 
              name: dateName, 
              centralBuy: centralBuyVal, 
              centralSell: centralSellVal, 
              // Promedios generales
              avgBuy: avgBuyAll,
              avgSell: avgSellAll,
              avgBuyAll: avgBuyAll, 
              avgSellAll: avgSellAll,
              // Promedios específicos
              avgBuyBanks: avgBuyBanks,
              avgSellBanks: avgSellBanks,
              avgBuyAsoc: avgBuyAsoc,
              avgSellAsoc: avgSellAsoc,
              avgBuyAgents: avgBuyAgents,
              avgSellAgents: avgSellAgents
          });
        });

        setCentralBankHistory(summaryHistoricalData);

        const latestSummary = summaryHistoricalData[summaryHistoricalData.length - 1];
        const previousSummary = summaryHistoricalData.length > 1 ? summaryHistoricalData[summaryHistoricalData.length - 2] : latestSummary;

        // El Banco Central no siempre ha publicado la tasa de vigencia de hoy
        // al momento en que carga la página (normalmente lo hacen en algún
        // punto de la mañana/tarde, no a medianoche — ver la nota de
        // extraer_banco_central en el scraper). En vez de esconder la
        // tarjeta y las brechas mientras tanto, se busca hacia atrás el día
        // más reciente que sí tenga un valor del BC, y se marca como
        // "desactualizado" para avisarlo en vez de mostrarlo como si fuera
        // el de hoy sin más.
        let indiceBC = summaryHistoricalData.length - 1;
        while (indiceBC >= 0 && summaryHistoricalData[indiceBC].centralBuy == null) { indiceBC--; }
        const centralSummary = indiceBC >= 0 ? summaryHistoricalData[indiceBC] : null;

        // Busca hacia atrás también para el punto de comparación — no basta
        // con "el día justo antes", porque ese también podría estar sin
        // publicar (ej. dos feriados seguidos).
        let indicePrevBC = indiceBC - 1;
        while (indicePrevBC >= 0 && summaryHistoricalData[indicePrevBC].centralBuy == null) { indicePrevBC--; }
        const centralPreviousSummary = indicePrevBC >= 0 ? summaryHistoricalData[indicePrevBC] : centralSummary;
        const centralBankDesactualizado = !!(centralSummary && centralSummary.name !== latestSummary.name);

        if (centralSummary) {
            setCentralBankData({
                buyRateUSD: { current: centralSummary.centralBuy.toFixed(2), previous: centralPreviousSummary.centralBuy.toFixed(2) },
                sellRateUSD: { current: centralSummary.centralSell.toFixed(2), previous: centralPreviousSummary.centralSell.toFixed(2) },
                fechaVigencia: centralSummary.name,
                desactualizado: centralBankDesactualizado
            });
        }
        
        if (latestSummary.avgBuyAll > 0) { 
            setAverageRates({ 
                buyRateUSD: { current: latestSummary.avgBuyAll.toFixed(2), previous: previousSummary.avgBuyAll.toFixed(2) }, 
                sellRateUSD: { current: latestSummary.avgSellAll.toFixed(2), previous: previousSummary.avgSellAll.toFixed(2) } 
            }); 
        }

        const latestEntities = allDaysEntities[allDaysEntities.length - 1].map(entity => ({ id: entity.nombre_entidad, ...entity }));
        const otherBanksFromFirestore = latestEntities.filter(d => d.nombre_entidad !== "Banco Central");
        
        const formattedBanks = otherBanksFromFirestore.map(bank => {
          const bankHistory = bankHistoricalData[bank.nombre_entidad] || [];
          const combinedHistory = bankHistory.map(bankDayData => {
            const summaryDayData = summaryHistoricalData.find(summaryDay => summaryDay.name === bankDayData.name);
            const centralBuy = summaryDayData ? summaryDayData.centralBuy : null;
            const centralSell = summaryDayData ? summaryDayData.centralSell : null;
            const avgBuy = summaryDayData ? summaryDayData.avgBuyAll : 0;
            const avgSell = summaryDayData ? summaryDayData.avgSellAll : 0;
            return {
                ...bankDayData,
                centralBuy,
                centralSell,
                avgBuy,
                avgSell,
                // null (no una resta contra 0) cuando el BC no tiene dato ese
                // día, o cuando la propia entidad no tiene esa tasa (ej.
                // Caribe Express nunca tiene venta) — mismo motivo que
                // arriba: evita una brecha falsa que arrastre el eje de su
                // propio gráfico.
                buyGap: (centralBuy != null && bankDayData.buy != null ? bankDayData.buy - centralBuy : null),
                sellGap: (centralSell != null && bankDayData.sell != null ? bankDayData.sell - centralSell : null)
            };
          });
          const latestRate = combinedHistory[combinedHistory.length - 1] || {};
          const previousRate = combinedHistory.length > 1 ? combinedHistory[combinedHistory.length - 2] : latestRate;
          return {
              id: bank.id,
              name: bank.nombre_entidad,
              logo: `https://placehold.co/40x40/E5E7EB/4B5563?text=${bank.nombre_entidad.charAt(0)}`,
              // null (no "0.00") cuando la entidad no tiene esa tasa —
              // ver RateValue, que ya sabe mostrar "-" en ese caso en vez
              // de un cero que parecería una tasa real.
              buyRateUSD: { current: latestRate.buy != null ? latestRate.buy.toFixed(2) : null, previous: previousRate.buy != null ? previousRate.buy.toFixed(2) : null },
              sellRateUSD: { current: latestRate.sell != null ? latestRate.sell.toFixed(2) : null, previous: previousRate.sell != null ? previousRate.sell.toFixed(2) : null },
              history: combinedHistory 
          }
        });

        const centralRateToday = centralSummary;
        if (centralRateToday) {
          // null (no "NaN") en cualquiera de los dos lados: cuando la
          // propia entidad no tiene esa tasa (ej. Caribe Express sin
          // venta) o, más raro, cuando el BC tampoco la tiene ese día.
          const banksWithGap = formattedBanks.map(bank => {
              const buyRate = bank.buyRateUSD.current != null ? parseFloat(bank.buyRateUSD.current) : null;
              const sellRate = bank.sellRateUSD.current != null ? parseFloat(bank.sellRateUSD.current) : null;
              const buyGapVal = (buyRate != null && centralRateToday.centralBuy != null) ? buyRate - centralRateToday.centralBuy : null;
              const sellGapVal = (sellRate != null && centralRateToday.centralSell != null) ? sellRate - centralRateToday.centralSell : null;
              return {
                  ...bank,
                  buyGap: buyGapVal != null ? buyGapVal.toFixed(2) : null,
                  sellGap: sellGapVal != null ? sellGapVal.toFixed(2) : null
              };
          });
          setBankData(banksWithGap);

          if (banksWithGap.length > 0) {
            // Compra y venta se evalúan por separado para "mejores tasas" —
            // una entidad que solo compra (ej. Caribe Express) debe poder
            // competir por "mejor para vender (compra)" aunque no tenga
            // tasa de venta con la que competir por el otro lado.
            const calcularMejoresTasas = (lista) => {
                const buyRates = lista
                    .map(b => ({ bank: b, rate: b.buyRateUSD.current != null ? parseFloat(b.buyRateUSD.current) : NaN }))
                    .filter(r => !isNaN(r.rate));
                const sellRates = lista
                    .map(b => ({ bank: b, rate: b.sellRateUSD.current != null ? parseFloat(b.sellRateUSD.current) : NaN }))
                    .filter(r => !isNaN(r.rate));

                const resultado = {};
                if (buyRates.length > 0) {
                    const maxBuyRate = Math.max(...buyRates.map(r => r.rate));
                    resultado.highestBuy = {
                        names: buyRates.filter(r => r.rate === maxBuyRate).map(r => r.bank.name),
                        rate: maxBuyRate.toFixed(2)
                    };
                }
                if (sellRates.length > 0) {
                    const minSellRate = Math.min(...sellRates.map(r => r.rate));
                    resultado.lowestSell = {
                        names: sellRates.filter(r => r.rate === minSellRate).map(r => r.bank.name),
                        rate: minSellRate.toFixed(2)
                    };
                }
                return (resultado.highestBuy || resultado.lowestSell) ? resultado : null;
            };

            // "Mejores tasas" ahora se calcula por separado para cada tipo de
            // entidad — antes del Banco Central, una asociación con una
            // tasa mediocre para su categoría podía ganarle a todo el
            // sistema solo por competir contra bancos y agentes de cambio
            // juntos; ahora cada bloque compite dentro de su propio grupo.
            const gruposDeEntidades = {
                banks: banksWithGap.filter(b => !isAssociationEntity(b.name) && !isExchangeAgentEntity(b.name)),
                associations: banksWithGap.filter(b => isAssociationEntity(b.name)),
                agents: banksWithGap.filter(b => isExchangeAgentEntity(b.name))
            };

            const nextBestRates = {};
            Object.entries(gruposDeEntidades).forEach(([clave, lista]) => {
                const resultado = calcularMejoresTasas(lista);
                if (resultado) nextBestRates[clave] = resultado;
            });

            if (Object.keys(nextBestRates).length > 0) {
                setBestRates(nextBestRates);
            }
          }
        } else { setBankData(formattedBanks); }

        if (dashboardData.lastUpdated) {
          const timestamp = new Date(dashboardData.lastUpdated);
          const formattedDate = timestamp.toLocaleDateString(t.dateLocale, t.dateOptions);
          setLastUpdated(formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1));
        }
        setError(null);
      } catch (err) { console.error("Error al obtener datos del panel:", err); setError(err.message); } finally { setLoading(false); }
    };
    fetchAllData();

    // Los datos solo cambian una vez al día, así que un refresco periódico
    // (en vez de una suscripción en tiempo real) es suficiente para mantener
    // "última actualización" al día sin recargar la página.
    const intervalId = setInterval(fetchAllData, 10 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [locale]);

  if (loading) return ( <div className="min-h-screen bg-paper dark:bg-ink flex items-center justify-center"> <div className="flex flex-col items-center"> <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brass-deep dark:border-brass"></div> <p className="mt-4 text-lg text-mist-2 dark:text-mist">{t.loading}</p> </div> </div> );
  if (error) return ( <div className="min-h-screen bg-paper dark:bg-ink flex items-center justify-center"> <div className="text-down text-center"> <h2 className="text-xl font-bold mb-2">{t.error}</h2> <p>{error}</p> </div> </div> );
  
  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');
  
  const ThemeToggle = () => (
    <button onClick={toggleTheme} className="p-2 rounded-full text-mist-2 dark:text-mist hover:bg-paper-line dark:hover:bg-ink-line transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brass-deep" aria-label="Toggle theme">
      {theme === 'light' ? ( <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path></svg> ) : ( <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="m17.66 17.66 1.41 1.41"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m4.93 19.07 1.41-1.41"></path><path d="m17.66 6.34 1.41-1.41"></path></svg> )}
    </button>
  );

  const LanguageSwitcher = () => (
    <div className="flex bg-paper-2 dark:bg-ink-2 rounded-full p-1 space-x-1 border border-paper-line dark:border-ink-line">
      <button onClick={() => setLocale('es')} className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors duration-200 ${locale === 'es' ? 'bg-brass-deep text-cream' : 'text-mist-2 dark:text-mist hover:bg-paper-line dark:hover:bg-ink-line'}`} aria-label="Cambiar a español">ES</button>
      <button onClick={() => setLocale('en')} className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors duration-200 ${locale === 'en' ? 'bg-brass-deep text-cream' : 'text-mist-2 dark:text-mist hover:bg-paper-line dark:hover:bg-ink-line'}`} aria-label="Switch to English">EN</button>
    </div>
  );

  return (
    <div className="min-h-screen font-sans bg-paper dark:bg-ink">
      <nav className="bg-paper-2 dark:bg-ink-2 p-4 border-b border-paper-line dark:border-ink-line">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-4">
          
          {/* NUEVO CONTENEDOR: Agrupa el logo y el título */}
          <div className="flex items-center space-x-3 md:space-x-4">
            
            {/* Logo Modo Claro: Visible por defecto, oculto en dark mode (dark:hidden) */}
            <img 
              src={logoLight} 
              alt="Logo Finanzas Criollas" 
              className="h-10 md:h-14 w-auto object-contain dark:hidden" 
            />

            {/* Logo Modo Oscuro: Oculto por defecto (hidden), visible en dark mode (dark:block) */}
            <img 
              src={logoDark} 
              alt="Logo Finanzas Criollas" 
              className="h-10 md:h-14 w-auto object-contain hidden dark:block" 
            />

            <h1 className="text-3xl md:text-5xl font-bold text-brass-deep dark:text-brass">
              {t.appTitle}
            </h1>
          </div>
          
          <div className="space-x-4 flex items-center">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </nav>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <header className="bg-ink text-cream p-6 sm:p-8 rounded-t-xl border-t-2 border-brass-deep">
            <h1 className="text-xl sm:text-4xl font-extrabold text-center mb-1 md:mb-2">{t.pageTitle}</h1>
            <p className="text-xs sm:text-xl text-mist text-center">{t.pageSubtitle}</p>
          </header>
          <main className="p-4 sm:p-6 lg:p-8 bg-paper-2 dark:bg-ink-2 rounded-b-xl border border-t-0 border-paper-line dark:border-ink-line">
            <Dashboard 
                bankData={bankData}
                centralBankData={centralBankData}
                centralBankHistory={centralBankHistory}
                averageRates={averageRates}
                bestRates={bestRates}
                loading={loading}
                lastUpdated={lastUpdated}
                locale={locale}
                isMobile={isMobile}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedSortOption={selectedSortOption}
                setSelectedSortOption={setSelectedSortOption}
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                theme={theme}
            />
          </main>
        </div>
      </div>
    </div>
  );
}

export default App;